import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from './LanguageContext';
import { getAuthUrl } from '../config/api.config';
import { BlobticDataManager } from '../services/blobticDataManager';
import { blobticRealtimeService } from '../services/blobticRealtimeService';
import { academyStore } from '../../Academy/services/academyStore';

// Types
interface User {
  id: number;
  code: string;
  name: string;
  email: string;
  profileId: number;
  agency: string;
  connectionId: string;
  language?: string;
  isAdmin?: boolean;
  profileLib?: string;
  dosCode?: string;
  dosIdf?: string;
  idxDBName?: string; // DB name précalculé par le serveur: USR_UIDF_DOS_UIDF_appName_IdxDB
  // boubaker 22/08/2026 - IndexDB multi-utilisateurs
  idxDbResetAll?: boolean; // true → vider tout l'IndexDB local (recharger depuis le serveur)
  idxDbClosed?: { suidbIdf?: number; objStore?: string; type?: string }[]; // stores à vider
  appName?: string;
  dosLib?: string;
  dosTelph?: string;
  dosEmail?: string;
  dosAdr1?: string;
  dosLogo?: string;
  dosClosedPlng?: string;
  warning?: string;
  Warning?: string;
}

export interface SsoProvider {
  id: string;
  name: string;
  icon: string;
  clientId: string;
  tenantId?: string;
  authority?: string;
}

export interface SsoExchangeRequest {
  provider: string;
  code: string;
  redirectUri?: string;
  state?: string;
  appName?: string;
  dosCode?: string;
  language?: string;
}

export interface SsoSelectAgencyRequest {
  email: string;
  agency: string;
  ssoProvider?: string;
  ssoId?: string;
  appName?: string;
  language?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
  agency: string;
  applicationName?: string;
  otpCode?: string;
  language?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresDoubleAuth: boolean;
  pendingCredentials: LoginCredentials | null;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  completeDoubleAuth: (otpCode: string) => Promise<boolean>;
  cancelDoubleAuth: () => void;
  logout: (silent?: boolean) => Promise<void>;
  getSsoProviders: () => Promise<SsoProvider[]>;
  getSsoAuthorizeUrl: (provider: string, appName?: string, dosCode?: string) => Promise<string | null>;
  ssoExchange: (data: SsoExchangeRequest) => Promise<{ success: boolean; requiresDossierSelection?: boolean; dossiers?: any[]; email?: string; ssoProvider?: string; ssoId?: string; error?: string }>;
  ssoSelectAgency: (data: SsoSelectAgencyRequest) => Promise<boolean>;
  error: string | null;
  clearError: () => void;
}

// Création du contexte
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider du contexte
interface AuthProviderProps {
  children: ReactNode;
}

// Global auth service for non-React context usage
export const globalAuthService = {
  logout: async () => { console.warn("[GlobalAuth] Logout not initialized"); },
  handleSessionExpired: () => { console.warn("[GlobalAuth] handleSessionExpired not initialized"); },
  t: (key: string) => key
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresDoubleAuth, setRequiresDoubleAuth] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<LoginCredentials | null>(null);
  
  const { setLanguage, language: currentLanguage, t } = useLanguage();

  const handleSessionExpired = () => {
    sessionStorage.removeItem('blobtic_token');
    sessionStorage.removeItem('blobtic_app_name');
    BlobticDataManager.clearSavedContext();
    blobticRealtimeService.disconnect();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setRequiresDoubleAuth(false);
    setPendingCredentials(null);

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicPage = pathname.startsWith('/student-pre-registration') ||
      pathname.startsWith('/quiz') ||
      pathname.startsWith('/academy-quiz');

    if (!isPublicPage && typeof window !== 'undefined' && !pathname.endsWith('/login')) {
      blobticMessage.error(t('auth.session_expired.title'), t('auth.session_expired.message'));

      const pathSegments = pathname.split('/');
      const appSegment = pathSegments[1];
      const targetApp = (appSegment && appSegment.length > 0 && appSegment !== 'login') ? appSegment : 'Planning';
      const targetLoginUrl = `/${targetApp}/login`;

      setTimeout(() => {
        window.location.href = targetLoginUrl;
      }, 2500);
    }
  };

  // Configuration d'axios & interceptor de session expirée
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        if (response.data && (response.data.code === 'SESSION_LOST' || response.data.valid === false)) {
          handleSessionExpired();
        }
        return response;
      },
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403 || error.response.data?.code === 'SESSION_LOST')) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    const token = sessionStorage.getItem('blobtic_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setIsLoading(false);
    }

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Vérification du token au démarrage
  const verifyToken = async () => {
    try {
      // Ajout des en-têtes de traçage pour le diagnostic et la langue
      const tracingHeaders = {
        'X-Computer-Name': navigator.platform || 'Unknown',
        'X-User-Language': currentLanguage || navigator.language || 'fr',
        'X-User-Agent': navigator.userAgent || '',
        'X-Referer': document.referrer || window.location.href,
        'X-Host': window.location.host || '',
        'X-IP-Address': 'auto-detected',
        'Accept-Language': currentLanguage || navigator.language || 'fr',
        'Referer': document.referrer || window.location.href
      };

      const response = await axios.get(getAuthUrl('VERIFY'), {
        headers: tracingHeaders
      });
      if (response.data.valid) {
        const rData = response.data;
        const rawAgency = rData.agencyInfo || rData.agency || rData.user?.agencyInfo || {};
        const userData: User = {
          id: parseInt(rData.userId || rData.id || '0'),
          code: rData.userCode || rData.code || '',
          name: rData.name || '',
          email: rData.email || '',
          profileId: parseInt(rData.profileId || '1'),
          agency: rData.agency || rawAgency.DOS_LIB || rawAgency.dosLib || '',
          connectionId: rData.connectionId || '',
          language: rData.language || 'fr',
          isAdmin: rData.isAdmin,
          profileLib: rData.profileLib || '',
          dosCode: rData.dosCode || rData.DOS_CODE || rawAgency.DOS_CODE || rawAgency.dosCode,
          dosIdf: rData.dosIdf || rData.DOS_IDF || rawAgency.DOS_IDF || rawAgency.dosIdf,
          idxDBName: rData.idxDBName || '',  // DB name précalculé par le serveur
          // boubaker 22/08/2026 - IndexDB multi-utilisateurs
          idxDbResetAll: Boolean(rData.idxDbResetAll),
          idxDbClosed: Array.isArray(rData.idxDbClosed) ? rData.idxDbClosed : undefined,
          appName: rData.appName || rData.APP_NAME || '',
          dosLib: rData.dosLib || rData.DOS_LIB || rawAgency.DOS_LIB || rawAgency.dosLib || rData.agency,
          dosTelph: rData.dosTelph || rData.DOS_TELPH || rawAgency.DOS_TELPH || rawAgency.dosTelph,
          dosEmail: rData.dosEmail || rData.DOS_EMAIL || rawAgency.DOS_EMAIL || rawAgency.dosEmail,
          dosAdr1: rData.dosAdr1 || rData.DOS_ADR1 || rawAgency.DOS_ADR1 || rawAgency.dosAdr1,
          dosLogo: rData.dosLogo || rData.DOS_LOGO || rawAgency.DOS_LOGO || rawAgency.dosLogo,
          dosClosedPlng: rData.dosClosedPlng || rData.DOS_CLOSEDPLNG || rawAgency.DOS_CLOSEDPLNG || rawAgency.dosClosedPlng,
        };

        if (response.data.language && response.data.language !== currentLanguage) {
          setLanguage(response.data.language);
        }

        if (userData.appName) {
          sessionStorage.setItem('blobtic_app_name', userData.appName);
        }

        setUser(userData);
        setIsAuthenticated(true);
      } else {
        handleSessionExpired();
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du token:', error);
      handleSessionExpired();
    } finally {
      setIsLoading(false);
    }
  };

  // Synchronisation dynamique du stock IndexedDB avec l'utilisateur connecté
  // idxDBName calculé par le serveur: USR_UIDF_DOS_UIDF_appName_IdxDB
  // boubaker 22/08/2026 - IndexDB multi-utilisateurs : applique les invalidations
  // renvoyées par le serveur (resetAll → tout vider ; idxDbClosed → stores à vider).
  const applyIdxDbSync = async (u: User | null): Promise<void> => {
    try {
      if (!BlobticDataManager.isValidContext()) return;
      const db = await BlobticDataManager.initDB(u?.idxDBName || '');
      if (!db) return;
      if (u?.idxDbResetAll) {
        // boubaker 22/08/2026 - IndexDB multi-utilisateurs : supprimer tout les object stores
        // (la base IndexDB est supprimée puis recréée dynamiquement au prochain accès).
        await BlobticDataManager.dropAllStores();
        return;
      }
      const closed = u?.idxDbClosed;
      if (Array.isArray(closed) && closed.length > 0) {
        await BlobticDataManager.removeStores(
          closed.map((c) => c?.objStore || '').filter((n) => n.length > 0)
        );
      }
    } catch { /* silencieux */ }
  };

  useEffect(() => {
    if (user) {
      // 100% Self-Hosted WebSockets Real-Time Connection
      const targetDosId = parseInt(String(user.dosIdf || '1'), 10) || 1;
      blobticRealtimeService.connect(targetDosId);

      // boubaker 25/08/2026 - Définir le contexte IndexedDB avec le nom renvoyé par le serveur.
      // Si idxDBName est vide (IDXDB_CACHE_ENABLED désactivé côté serveur), BlobticDataManager est désactivé.
      BlobticDataManager.setContext(user.idxDBName || '');

      if (BlobticDataManager.isValidContext()) {
        applyIdxDbSync(user).then(() => {
          academyStore.initFromDb(
            user.code || String(user.id),
            user.dosIdf || ''
          );
        });
      } else {
        academyStore.initFromDb(
          user.code || String(user.id),
          user.dosIdf || ''
        );
      }
    } else {
      blobticRealtimeService.disconnect();
    }
  }, [user]);

  // Connexion avec double authentification
  const completeDoubleAuth = async (otpCode: string): Promise<boolean> => {
    try {
      if (!pendingCredentials) {
        setError(t('auth.error.noPending'));
        return false;
      }

      setIsLoading(true);
      setError(null);

      const credentialsWithOtp = {
        ...pendingCredentials,
        otpCode: otpCode,
        language: pendingCredentials.language || navigator.language
      };

      // Ajout des en-têtes de traçage pour le diagnostic
      const tracingHeaders = {
        'X-Computer-Name': navigator.platform || 'Unknown',
        'X-User-Language': currentLanguage || navigator.language || 'fr',
        'X-User-Agent': navigator.userAgent || '',
        'X-Referer': document.referrer || window.location.href,
        'X-Host': window.location.host || '',
        'X-IP-Address': 'auto-detected',
        'Accept-Language': currentLanguage || navigator.language || 'fr',
        'Referer': document.referrer || window.location.href
      };

      const response = await axios.post(getAuthUrl('LOGIN'), credentialsWithOtp, {
        headers: tracingHeaders
      });

      if (response.data.token) {
        const { token, user: rawUser } = response.data;
        const rAgency = response.data.agencyInfo || response.data.agency || rawUser?.agencyInfo || {};
        const userData: User = {
          ...rawUser,
          dosCode: rawUser?.dosCode || rawUser?.DOS_CODE || rAgency.DOS_CODE || rAgency.dosCode,
          dosIdf: rawUser?.dosIdf || rawUser?.DOS_IDF || rAgency.DOS_IDF || rAgency.dosIdf,
          idxDBName: rawUser?.idxDBName || '',  // DB name précalculé par le serveur
          // boubaker 22/08/2026 - IndexDB multi-utilisateurs
          idxDbResetAll: Boolean(rawUser?.idxDbResetAll),
          idxDbClosed: Array.isArray(rawUser?.idxDbClosed) ? rawUser.idxDbClosed : undefined,
          appName: rawUser?.appName || rawUser?.APP_NAME || pendingCredentials?.applicationName || rAgency.APP_NAME || '',
          dosLib: rawUser?.dosLib || rawUser?.DOS_LIB || rAgency.DOS_LIB || rAgency.dosLib || rawUser?.agency,
          dosTelph: rawUser?.dosTelph || rawUser?.DOS_TELPH || rAgency.DOS_TELPH || rAgency.dosTelph,
          dosEmail: rawUser?.dosEmail || rawUser?.DOS_EMAIL || rAgency.DOS_EMAIL || rAgency.dosEmail,
          dosAdr1: rawUser?.dosAdr1 || rawUser?.DOS_ADR1 || rAgency.DOS_ADR1 || rAgency.dosAdr1,
          dosLogo: rawUser?.dosLogo || rawUser?.DOS_LOGO || rAgency.DOS_LOGO || rAgency.dosLogo,
          dosClosedPlng: rawUser?.dosClosedPlng || rawUser?.DOS_CLOSEDPLNG || rAgency.DOS_CLOSEDPLNG || rAgency.dosClosedPlng,
        };

        // Stockage du token
        sessionStorage.setItem('blobtic_token', token);
        if (userData.appName) {
          sessionStorage.setItem('blobtic_app_name', userData.appName);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Mise à jour de l'état
        setUser(userData);

        // Sync language from user profile
        if (userData.language) {
          setLanguage(userData.language as any);
        }

        setIsAuthenticated(true);
        setRequiresDoubleAuth(false);
        setPendingCredentials(null);

        // Check for expiration warning
        const warningKey = userData.warning || userData.Warning;
        if (warningKey) {
          blobticMessage.warning(
            t('common.messages.warning'), // Or a specific title for password expiration default
            t(warningKey));
        }

        // Notification de succès
        blobticMessage.success(
          t('auth.success.login.title'),
          t('auth.success.login.welcome', userData.name || userData.email));

        return true;
      } else {
        const errorMsg = t('auth.error.invalidResponse');
        setError(errorMsg);
        blobticMessage.error(t('auth.error.loginTitle'), errorMsg);
        return false;
      }
    } catch (error: any) {
      console.error('Erreur de double authentification:', error);

      let errorTitle = t('auth.error.doubleAuthTitle');
      let errorMessage = t('auth.error.unexpected');

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 401:
            errorMessage = data.error || t('auth.error.invalidOtpDesc');
            break;
          case 400:
            errorMessage = data.error || t('auth.error.credentials');
            break;
          case 500:
            errorMessage = t('auth.error.server');
            break;
          default:
            errorMessage = data.error || `Erreur ${status}`;
        }
      } else if (error.request) {
        errorMessage = t('auth.error.network');
      }

      setError(errorMessage);
      blobticMessage.error(errorTitle, errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Annuler la double authentification
  const cancelDoubleAuth = () => {
    setRequiresDoubleAuth(false);
    setPendingCredentials(null);
    setError(null);
  };

  // Connexion
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // Ajout des en-têtes de traçage pour le diagnostic
      const tracingHeaders = {
        'X-Computer-Name': navigator.platform || 'Unknown',
        'X-User-Language': currentLanguage || navigator.language || 'fr',
        'X-User-Agent': navigator.userAgent || '',
        'X-Referer': document.referrer || window.location.href,
        'X-Host': window.location.host || '',
        'X-IP-Address': 'auto-detected',
        'Accept-Language': currentLanguage || navigator.language || 'fr',
        'Referer': document.referrer || window.location.href
      };

      const response = await axios.post(getAuthUrl('LOGIN'), credentials, {
        headers: tracingHeaders
      });

      // Vérifier si la double authentification est requise
      if (response.data.requiresDoubleAuth) {
        setRequiresDoubleAuth(true);
        setPendingCredentials(credentials);
        blobticMessage.success(
          t('auth.info.doubleAuthRequired'),
          t('auth.info.otpSent'));
        return false;
      }

      if (response.data.token) {
        const { token, user: rawUser } = response.data;
        const rAgency = response.data.agencyInfo || response.data.agency || rawUser?.agencyInfo || {};
        const userData: User = {
          ...rawUser,
          dosCode: rawUser?.dosCode || rawUser?.DOS_CODE || rAgency.DOS_CODE || rAgency.dosCode,
          dosIdf: rawUser?.dosIdf || rawUser?.DOS_IDF || rAgency.DOS_IDF || rAgency.dosIdf,
          idxDBName: rawUser?.idxDBName || '',  // DB name précalculé par le serveur
          // boubaker 22/08/2026 - IndexDB multi-utilisateurs
          idxDbResetAll: Boolean(rawUser?.idxDbResetAll),
          idxDbClosed: Array.isArray(rawUser?.idxDbClosed) ? rawUser.idxDbClosed : undefined,
          appName: rawUser?.appName || rawUser?.APP_NAME || credentials.applicationName || rAgency.APP_NAME || '',
          dosLib: rawUser?.dosLib || rawUser?.DOS_LIB || rAgency.DOS_LIB || rAgency.dosLib || rawUser?.agency,
          dosTelph: rawUser?.dosTelph || rawUser?.DOS_TELPH || rAgency.DOS_TELPH || rAgency.dosTelph,
          dosEmail: rawUser?.dosEmail || rawUser?.DOS_EMAIL || rAgency.DOS_EMAIL || rAgency.dosEmail,
          dosAdr1: rawUser?.dosAdr1 || rawUser?.DOS_ADR1 || rAgency.DOS_ADR1 || rAgency.dosAdr1,
          dosLogo: rawUser?.dosLogo || rawUser?.DOS_LOGO || rAgency.DOS_LOGO || rAgency.dosLogo,
          dosClosedPlng: rawUser?.dosClosedPlng || rawUser?.DOS_CLOSEDPLNG || rAgency.DOS_CLOSEDPLNG || rAgency.dosClosedPlng,
        };

        // Stockage du token
        sessionStorage.setItem('blobtic_token', token);
        if (userData.appName) {
          sessionStorage.setItem('blobtic_app_name', userData.appName);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Mise à jour de l'état
        setUser(userData);

        // Sync language from user profile
        if (userData.language) {
          setLanguage(userData.language as any);
        }

        setIsAuthenticated(true);
        setIsAuthenticated(true);
        setRequiresDoubleAuth(false);
        setPendingCredentials(null);

        // Check for expiration warning
        const warningKey = userData.warning || userData.Warning;
        if (warningKey) {
          blobticMessage.warning(
            t('common.messages.warning'), // Or a specific title for password expiration default
            t(warningKey));
        }

        // Notification de succès
        blobticMessage.success(
          t('auth.success.login.title'),
          t('auth.success.login.welcome', userData.name || userData.email));

        return true;
      } else {
        const errorMsg = t('auth.error.invalidResponse');
        setError(errorMsg);
        blobticMessage.error(t('auth.error.loginTitle'), errorMsg);
        return false;
      }
    } catch (error: any) {
      console.error('Erreur de connexion:', error);

      let errorTitle = t('auth.error.loginTitle');
      let errorMessage = t('auth.error.unexpected');

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorTitle = t('auth.error.credentialsTitle');
        errorMessage = t('auth.error.credentials');
      } else if (error.response?.status === 500) {
        errorTitle = t('common.messages.error'); // Using generic error for title
        errorMessage = t('auth.error.server');
      } else if (error.code === 'NETWORK_ERROR') {
        errorTitle = t('common.messages.error'); // Using generic error for title
        errorMessage = t('auth.error.network');
      }

      // Gestion spécifique des erreurs de double authentification
      if (error.response?.data?.code === 'OTP_BLOCKED_RECENTLY') {
        errorTitle = t('auth.error.otpBlockedRecentlyTitle');
        errorMessage = t('auth.error.otpBlockedRecentlyDesc');
      } else if (error.response?.data?.code === 'OTP_BLOCKED') {
        errorTitle = t('auth.error.otpBlockedTitle');
        errorMessage = t('auth.error.otpBlockedDesc');
      } else if (error.response?.data?.code === 'OTP_EXPIRED') {
        errorTitle = t('auth.error.otpExpiredTitle');
        errorMessage = t('auth.error.otpExpiredDesc');
      } else if (error.response?.data?.code === 'INVALID_OTP') {
        errorTitle = t('auth.error.invalidOtpTitle');
        errorMessage = t('auth.error.invalidOtpDesc');
      } else if (error.response?.data?.code === 'OTP_SEND_ERROR') {
        errorMessage = t('error.otp.send');
      }

      setError(errorMessage);
      blobticMessage.error(errorTitle, errorMessage);

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Déconnexion
  const logout = async (silent: boolean = false): Promise<void> => {
    try {
      const token = sessionStorage.getItem('blobtic_token');
      if (token) {
        await axios.post(getAuthUrl('LOGOUT')).catch(() => { });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyage local
      sessionStorage.removeItem('blobtic_token');
      sessionStorage.removeItem('blobtic_app_name');
      BlobticDataManager.clearSavedContext();
      blobticRealtimeService.disconnect();
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      setRequiresDoubleAuth(false);
      setPendingCredentials(null);

      // Notification de déconnexion si non silencieux
      if (!silent) {
        blobticMessage.success(t('auth.success.logout.title'), t('auth.success.logout.message'));
      }
    }
  };

  // Link global service
  useEffect(() => {
    globalAuthService.logout = logout;
    globalAuthService.handleSessionExpired = handleSessionExpired;
    globalAuthService.t = t;
  }, [t]);

  // Effacement des erreurs
  const clearError = () => {
    setError(null);
  };

  // Méthodes SSO (Single Sign-On)
  const getSsoProviders = async (): Promise<SsoProvider[]> => {
    try {
      const response = await axios.get(getAuthUrl('SSO_PROVIDERS'));
      if (response.data && Array.isArray(response.data.providers)) {
        return response.data.providers;
      }
      return [];
    } catch (err) {
      console.warn('Erreur lors de la récupération des fournisseurs SSO:', err);
      return [];
    }
  };

  const getSsoAuthorizeUrl = async (provider: string, appName?: string, dosCode?: string): Promise<string | null> => {
    try {
      const targetApp = appName || 'Planning';
      const redirectUri = `${window.location.origin}/${targetApp}/auth/sso/callback`;
      const response = await axios.get(`${getAuthUrl('SSO_AUTHORIZE')}/${provider}`, {
        params: {
          appName: targetApp,
          dosCode: dosCode || '',
          redirectUri
        }
      });
      return response.data?.authorizationUrl || null;
    } catch (err) {
      console.error('Erreur lors de la génération de l\'URL d\'autorisation SSO:', err);
      return null;
    }
  };

  const ssoExchange = async (data: SsoExchangeRequest): Promise<{ success: boolean; requiresDossierSelection?: boolean; dossiers?: any[]; email?: string; ssoProvider?: string; ssoId?: string; error?: string }> => {
    try {
      setIsLoading(true);
      setError(null);

      const targetApp = data.appName || 'Planning';
      const redirectUri = data.redirectUri || `${window.location.origin}/${targetApp}/auth/sso/callback`;

      const response = await axios.post(getAuthUrl('SSO_EXCHANGE'), {
        ...data,
        redirectUri,
        language: data.language || currentLanguage || 'fr'
      });

      // Cas où plusieurs agences sont disponibles et nécessitent un choix
      if (response.data?.requiresDossierSelection) {
        setIsLoading(false);
        return {
          success: false,
          requiresDossierSelection: true,
          dossiers: response.data.dossiers || [],
          email: response.data.email,
          ssoProvider: response.data.ssoProvider,
          ssoId: response.data.ssoId
        };
      }

      if (response.data?.token) {
        const { token, user: rawUser } = response.data;
        const rAgency = response.data.agencyInfo || response.data.agency || rawUser?.agencyInfo || {};
        const userData: User = {
          ...rawUser,
          dosCode: rawUser?.dosCode || rawUser?.DOS_CODE || rAgency.DOS_CODE || rAgency.dosCode,
          dosIdf: rawUser?.dosIdf || rawUser?.DOS_IDF || rAgency.DOS_IDF || rAgency.dosIdf,
          idxDBName: rawUser?.idxDBName || '',
          idxDbResetAll: Boolean(rawUser?.idxDbResetAll),
          idxDbClosed: Array.isArray(rawUser?.idxDbClosed) ? rawUser.idxDbClosed : undefined,
          appName: rawUser?.appName || rawUser?.APP_NAME || data.appName || rAgency.APP_NAME || '',
          dosLib: rawUser?.dosLib || rawUser?.DOS_LIB || rAgency.DOS_LIB || rAgency.dosLib || rawUser?.agency,
          dosTelph: rawUser?.dosTelph || rawUser?.DOS_TELPH || rAgency.DOS_TELPH || rAgency.dosTelph,
          dosEmail: rawUser?.dosEmail || rawUser?.DOS_EMAIL || rAgency.DOS_EMAIL || rAgency.dosEmail,
          dosAdr1: rawUser?.dosAdr1 || rawUser?.DOS_ADR1 || rAgency.DOS_ADR1 || rAgency.dosAdr1,
          dosLogo: rawUser?.dosLogo || rawUser?.DOS_LOGO || rAgency.DOS_LOGO || rAgency.dosLogo,
          dosClosedPlng: rawUser?.dosClosedPlng || rawUser?.DOS_CLOSEDPLNG || rAgency.DOS_CLOSEDPLNG || rAgency.dosClosedPlng,
        };

        sessionStorage.setItem('blobtic_token', token);
        if (userData.appName) {
          sessionStorage.setItem('blobtic_app_name', userData.appName);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setUser(userData);
        if (userData.language) {
          setLanguage(userData.language as any);
        }

        setIsAuthenticated(true);
        blobticMessage.success(
          t('auth.success.login.title'),
          t('auth.success.login.welcome', userData.name || userData.email)
        );

        return { success: true };
      }

      const errMsg = response.data?.error || t('login.sso.error');
      setError(errMsg);
      return { success: false, error: errMsg };
    } catch (err: any) {
      const errMsg = err.response?.data?.error || t('login.sso.error');
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const ssoSelectAgency = async (data: SsoSelectAgencyRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.post(getAuthUrl('SSO_SELECT_AGENCY'), {
        ...data,
        language: data.language || currentLanguage || 'fr'
      });

      if (response.data?.token) {
        const { token, user: rawUser } = response.data;
        const rAgency = response.data.agencyInfo || response.data.agency || rawUser?.agencyInfo || {};
        const userData: User = {
          ...rawUser,
          dosCode: rawUser?.dosCode || rawUser?.DOS_CODE || rAgency.DOS_CODE || rAgency.dosCode,
          dosIdf: rawUser?.dosIdf || rawUser?.DOS_IDF || rAgency.DOS_IDF || rAgency.dosIdf,
          idxDBName: rawUser?.idxDBName || '',
          idxDbResetAll: Boolean(rawUser?.idxDbResetAll),
          idxDbClosed: Array.isArray(rawUser?.idxDbClosed) ? rawUser.idxDbClosed : undefined,
          appName: rawUser?.appName || rawUser?.APP_NAME || data.appName || rAgency.APP_NAME || '',
          dosLib: rawUser?.dosLib || rawUser?.DOS_LIB || rAgency.DOS_LIB || rAgency.dosLib || rawUser?.agency,
          dosTelph: rawUser?.dosTelph || rawUser?.DOS_TELPH || rAgency.DOS_TELPH || rAgency.dosTelph,
          dosEmail: rawUser?.dosEmail || rawUser?.DOS_EMAIL || rAgency.DOS_EMAIL || rAgency.dosEmail,
          dosAdr1: rawUser?.dosAdr1 || rawUser?.DOS_ADR1 || rAgency.DOS_ADR1 || rAgency.dosAdr1,
          dosLogo: rawUser?.dosLogo || rawUser?.DOS_LOGO || rAgency.DOS_LOGO || rAgency.dosLogo,
          dosClosedPlng: rawUser?.dosClosedPlng || rawUser?.DOS_CLOSEDPLNG || rAgency.DOS_CLOSEDPLNG || rAgency.dosClosedPlng,
        };

        sessionStorage.setItem('blobtic_token', token);
        if (userData.appName) {
          sessionStorage.setItem('blobtic_app_name', userData.appName);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        setUser(userData);
        if (userData.language) {
          setLanguage(userData.language as any);
        }

        setIsAuthenticated(true);
        blobticMessage.success(
          t('auth.success.login.title'),
          t('auth.success.login.welcome', userData.name || userData.email)
        );

        return true;
      }

      const errMsg = response.data?.error || t('login.sso.error');
      setError(errMsg);
      blobticMessage.error(t('login.sso.error'), errMsg);
      return false;
    } catch (err: any) {
      const errMsg = err.response?.data?.error || t('login.sso.error');
      setError(errMsg);
      blobticMessage.error(t('login.sso.error'), errMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    requiresDoubleAuth,
    pendingCredentials,
    login,
    completeDoubleAuth,
    cancelDoubleAuth,
    logout,
    getSsoProviders,
    getSsoAuthorizeUrl,
    ssoExchange,
    ssoSelectAgency,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
